import { Head } from "@inertiajs/react"

import { LandingPageCtaClicks } from "@/components/landing-page-cta-clicks"
import AttributeDisplay from "@/components/ui/attribute-display"
import AttributesContainer from "@/components/ui/attributes-container"
import LogoPreview from "@/components/ui/logo-preview"
import RecordPageHeader from "@/components/ui/record-page-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminAccountPath,
  adminPartnerPath,
  adminPartnersPath,
  editAdminPartnerPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Partner {
  uid: string
  name: string
  description: string | null
  state: string
  billing_email: string | null
  billing_type: "imperative_managed" | "partner_managed"
  accounts_count: number
  partner_users_count: number
  logo_url: string | null
  subdomain: string | null
  // IMP-287: the partner's public general-receptionist landing page (/p/:uid).
  landing_page_url: string | null
  created_at: string
}

interface LandingPageSession {
  id: number
  created_at: string
  account: { uid: string; name: string }
  location: string | null
  ip_address: string | null
  cta_counts: Record<string, number>
}

const BILLING_TYPE_LABEL: Record<Partner["billing_type"], string> = {
  imperative_managed: "Imperative-managed",
  partner_managed: "Partner-managed",
}

function formatSessionTime(iso: string) {
  const d = new Date(iso)
  return {
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    date: d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  }
}

export default function PartnerShow({
  partner,
  landing_page_sessions,
}: {
  partner: Partner
  landing_page_sessions: LandingPageSession[]
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Partners", href: adminPartnersPath() },
    { title: partner.name, href: "#" },
  ]

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={partner.name} />
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-6 p-4">
        <RecordPageHeader
          title={partner.name}
          state={partner.state}
          editPath={editAdminPartnerPath({ id: partner.uid })}
          deletePath={adminPartnerPath({ id: partner.uid })}
        />
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Details</h2>
          <AttributesContainer className="gap-6">
            {partner.logo_url && (
              <AttributeDisplay
                label="Logo"
                value={
                  <LogoPreview src={partner.logo_url} alt={partner.name} />
                }
              />
            )}
            {partner.description && (
              <AttributeDisplay
                label="Description"
                value={partner.description}
              />
            )}
            <AttributeDisplay
              label="Subdomain"
              value={partner.subdomain ?? "—"}
            />
            <AttributeDisplay
              label="Billing type"
              value={BILLING_TYPE_LABEL[partner.billing_type]}
            />
            {partner.billing_email && (
              <AttributeDisplay
                label="Billing email"
                value={partner.billing_email}
              />
            )}
            {partner.landing_page_url && (
              <AttributeDisplay
                label="Receptionist landing page"
                value={
                  <a
                    href={partner.landing_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary break-all underline underline-offset-2"
                  >
                    {partner.landing_page_url}
                  </a>
                }
              />
            )}
            <AttributeDisplay
              label="Accounts"
              value={String(partner.accounts_count)}
            />
            <AttributeDisplay
              label="Team members"
              value={String(partner.partner_users_count)}
            />
            <AttributeDisplay
              label="Joined"
              value={new Date(partner.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            />
          </AttributesContainer>
        </div>

        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <h2 className="text-base font-semibold">Landing page sessions</h2>
            {landing_page_sessions.length > 0 && (
              <span className="text-muted-foreground text-sm">
                {landing_page_sessions.length === 50
                  ? "50+ sessions"
                  : `${landing_page_sessions.length} session${landing_page_sessions.length === 1 ? "" : "s"}`}
              </span>
            )}
          </div>
          {landing_page_sessions.length === 0 ? (
            <p className="text-muted-foreground text-sm">No sessions yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Visitor</TableHead>
                  <TableHead>CTA clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {landing_page_sessions.map((session) => {
                  const { time, date } = formatSessionTime(session.created_at)
                  return (
                    <TableRow key={session.id}>
                      <TableCell className="text-sm">
                        <a
                          href={adminAccountPath({ id: session.account.uid })}
                          className="underline underline-offset-2"
                        >
                          {session.account.name}
                        </a>
                        <div className="text-muted-foreground">
                          {time}, {date}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>
                          {session.ip_address ?? (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {session.location ?? "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <LandingPageCtaClicks ctaCounts={session.cta_counts} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
